/**
 * @architecture-header 1
 * @layer module-ui
 * @domain settings-invitations
 * @responsibility Render the SET-F06 Invitations pane from backend-authoritative invitation hooks.
 * @state-owner TanStack Query; local status filter and just-revealed-token state
 * @allowed-imports same-module hooks/components
 * @forbidden direct API client; hardcoded organization data; email-sending adapters
 * @public-api InvitationsSettingsPage
 * @side-effects invokes authorized same-module commands and cache invalidation
 * @tests InvitationsSettingsPage.test.tsx, InvitationsSettingsSurface.test.tsx
 */

import type { OrganizationInvitation } from '@vispo/contracts';
import { useState } from 'react';
import {
  useCreateOrganizationInvitation,
  useOrganizationInvitations,
  useOrganizationInvitationsAccess,
  useResendOrganizationInvitation,
  useRevokeOrganizationInvitation,
} from '../api/settings-invitations.queries';
import { InvitationsSettingsSurface } from './InvitationsSettingsSurface';

type StatusFilter = 'pending' | 'accepted' | 'declined' | 'revoked' | 'expired' | 'all';

function errorCodeOf(error: unknown): string | null {
  return error && typeof error === 'object' && 'code' in error ? String(error.code) : null;
}

export function InvitationsSettingsPage({ mobile }: { mobile: boolean }) {
  const [status, setStatus] = useState<StatusFilter>('pending');
  const [revealedToken, setRevealedToken] = useState<{
    invitationId: string;
    token: string;
  } | null>(null);
  const access = useOrganizationInvitationsAccess();
  const canManage = access.data?.canManage ?? false;
  const invitationsQuery = useOrganizationInvitations(
    access.data?.organizationId ?? null,
    status,
    canManage,
  );
  const create = useCreateOrganizationInvitation(status);
  const resend = useResendOrganizationInvitation(status);
  const revoke = useRevokeOrganizationInvitation(status);

  const commandError = create.error ?? resend.error ?? revoke.error;
  const errorCode = access.isError
    ? (errorCodeOf(access.error) ?? 'ORGANIZATION_INVITATIONS_UNAVAILABLE')
    : access.isSuccess && !canManage
      ? 'ORGANIZATION_INVITATION_ADMIN_REQUIRED'
      : (errorCodeOf(commandError) ?? errorCodeOf(invitationsQuery.error));

  const revealToken = (invitation: OrganizationInvitation) => {
    if (invitation.token)
      setRevealedToken({ invitationId: invitation.id, token: invitation.token });
  };

  return (
    <InvitationsSettingsSurface
      canManage={canManage}
      commandError={commandError}
      errorCode={errorCode}
      invitations={invitationsQuery.data?.invitations ?? null}
      loading={access.isPending || (canManage && invitationsQuery.isPending)}
      mobile={mobile}
      revealedToken={revealedToken}
      status={status}
      onCreate={(input) => {
        setRevealedToken(null);
        create.mutate(input, { onSuccess: revealToken });
      }}
      onResend={(invitation) => {
        setRevealedToken(null);
        resend.mutate(
          { invitationId: invitation.id, expectedRevision: invitation.revision },
          { onSuccess: revealToken },
        );
      }}
      onRetry={() => {
        create.reset();
        resend.reset();
        revoke.reset();
        void access.refetch();
        void invitationsQuery.refetch();
      }}
      onRevoke={(invitation) => {
        setRevealedToken(null);
        revoke.mutate({ invitationId: invitation.id, expectedRevision: invitation.revision });
      }}
      onStatusChange={setStatus}
      saving={create.isPending || resend.isPending || revoke.isPending}
    />
  );
}
